﻿import { MIN_ROUND_TIME_MS } from '../config';
import type { GameResult, SensorMode } from '../types';

export class RoundTimer {
  private startedAtMs = 0;
  private pausedAtMs: number | null = null;
  private pausedTotalMs = 0;

  start(now = performance.now()): void {
    this.startedAtMs = now;
    this.pausedAtMs = null;
    this.pausedTotalMs = 0;
  }

  pause(now = performance.now()): void {
    if (this.startedAtMs === 0 || this.pausedAtMs !== null) return;
    this.pausedAtMs = now;
  }

  resume(now = performance.now()): void {
    if (this.pausedAtMs === null) return;
    this.pausedTotalMs += Math.max(0, now - this.pausedAtMs);
    this.pausedAtMs = null;
  }

  get startedAt(): number {
    return this.startedAtMs;
  }

  elapsed(now = performance.now()): number {
    if (this.startedAtMs === 0) return 0;
    const pauseAdjustment = this.pausedAtMs === null ? this.pausedTotalMs : this.pausedTotalMs + Math.max(0, now - this.pausedAtMs);
    return Math.max(0, now - this.startedAtMs - pauseAdjustment);
  }
}

export function averageJumpsPerMinute(jumpCount: number, elapsedMs: number): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  return (Math.max(0, jumpCount) / elapsedMs) * 60_000;
}

export function createResult(input: {
  startedAt: number;
  endedAt: number;
  elapsedMs: number;
  jumpCount: number;
  targetJumps: number;
  defeated: boolean;
  sensorMode: SensorMode;
}): GameResult {
  const suspicion = input.defeated
    ? input.elapsedMs < MIN_ROUND_TIME_MS
      ? 'too_fast'
      : 'normal'
    : 'incomplete';

  return {
    ...input,
    jumpCount: Math.max(0, Math.floor(input.jumpCount)),
    targetJumps: Math.max(1, Math.floor(input.targetJumps)),
    elapsedMs: Math.max(0, input.elapsedMs),
    averageJumpsPerMinute: averageJumpsPerMinute(input.jumpCount, input.elapsedMs),
    suspicion
  };
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}